import Image from "next/image";
import Link from "next/link";

import logo from "@/assets/images/logo-vivaldis.svg";
import { DateLabel } from "@/components/atoms/DateLabel";
import Tag from "@/components/atoms/Tag";
import { BlogPageFieldsFragment } from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import type { AllowedLanguages } from "@/types/generic";

type XXX = NonNullable<BlogPageFieldsFragment["blogCategoryRef"]>[number];

type XXX2 = Omit<XXX, "tid">;

interface BlogCardItem {
  blogCategoryRef:
    | ({ id: string; name: string } | { __typename?: string })[]
    | null;
  created: {
    timestamp: number;
  };
  id: BlogPageFieldsFragment["id"];
  intro: BlogPageFieldsFragment["intro"];
  path: BlogPageFieldsFragment["path"];
  teaserMedia: BlogPageFieldsFragment["teaserMedia"];
  title: BlogPageFieldsFragment["title"];
}

interface BlogCardProps {
  blog: BlogCardItem;
  extraClasses?: string;
  i18n: {
    read_more: string;
  };
  locale: AllowedLanguages;
}

export const BlogCard = ({
  locale,
  blog,
  i18n,
  extraClasses,
}: BlogCardProps) => {
  const blogDate = new Date(blog.created.timestamp * 1000);
  const formattedBlogDate = blogDate.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return (
    <div
      key={blog.id}
      className={cn(
        extraClasses,
        "blog-teaser relative flex flex-col items-stretch rounded-lg border bg-white transition-all hover:border-yellow-600 hover:shadow-lg",
      )}
    >
      <div className="relative flex aspect-video w-full shrink-0 items-center justify-center overflow-hidden rounded-t-lg bg-yellow">
        {blog.teaserMedia?.imageMediaImage?.variations?.[2] ? (
          <Image
            src={blog.teaserMedia.imageMediaImage.variations[2].url} // Use the URL of the first variation
            fill
            alt={blog.title}
            sizes="450px"
            className={"absolute h-full w-full object-cover"}
          />
        ) : (
          <Image
            src={logo}
            width={80}
            height={42}
            alt="Vivaldis Interim"
            className="w-[160px]"
          />
        )}
      </div>

      <div className="flex flex-1">
        <div className="flex h-full w-full flex-col p-6 lg:p-8">
          {formattedBlogDate && (
            <DateLabel
              dateLabel={formattedBlogDate}
              className="relative z-10 text-base"
              iconClassName="text-green"
            />
          )}

          {blog.blogCategoryRef && (
            <div className="mb-4 mt-2 flex flex-wrap gap-2">
              {blog.blogCategoryRef
                .filter((category) => "id" in category)
                .map((category) => {
                  return (
                    <Tag
                      key={category.id}
                      color={"gray"}
                      icon={"tag"}
                      small={true}
                    >
                      {category.name}
                    </Tag>
                  );
                })}
            </div>
          )}

          {blog.title && (
            <h4 className={cn("h5 !mb-1 mt-3 break-words")}>{blog.title}</h4>
          )}

          {blog.intro && (
            <div
              className={cn(
                "prose mb-4 mt-2 line-clamp-2 max-w-full max-w-none text-sm lg:text-base",
              )}
              dangerouslySetInnerHTML={{
                __html:
                  blog.intro.processed?.replace(/<\/?[^>]+(>|$)/g, "") ?? "",
              }}
            ></div>
          )}

          <Link
            href={blog.path}
            className="stretched-link mr-auto mt-auto pt-4"
          >
            <span className="link pb-1 text-sm no-underline">
              {i18n.read_more}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};
